/**
 * Full-page note editor. Replaces the previous modal "edit note" experience.
 *
 * Features:
 *  - Rich text formatting (bold, italic, underline, strikethrough, color, font)
 *  - Interactive checkbox lists
 *  - File attachments (any type)
 *  - Auto-save on blur
 *  - Admin-only "assigned user" picker
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  ArrowLeft, FloppyDisk, Trash, TextB, TextItalic, TextUnderline, TextStrikethrough,
  CheckSquare, Paperclip, Download, X, ListBullets, ListNumbers, PaintBucket,
} from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#0F172A', '#1565C0', '#2E7D32', '#EF6C00', '#C62828', '#7B1FA2', '#F59E0B', '#5E5F66'];
const FONTS = [
  { label: 'Système', value: 'system-ui, sans-serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Mono', value: 'ui-monospace, "SF Mono", Menlo, monospace' },
];
const ALLOWED_TAGS = ['p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'ul', 'ol', 'li', 'font', 'h1', 'h2', 'h3', 'a', 'input', 'label'];
const ALLOWED_ATTR = ['style', 'color', 'face', 'href', 'target', 'rel', 'type', 'checked', 'class', 'data-checked'];
const sanitize = (html) => DOMPurify.sanitize(html || '', { ALLOWED_TAGS, ALLOWED_ATTR });

const ToolbarBtn = ({ onPress, title, active, children, testId }) => (
  <button
    type="button"
    title={title}
    data-testid={testId}
    onMouseDown={(e) => { e.preventDefault(); onPress(); }}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 32, height: 32, padding: 0, borderRadius: 6,
      border: '1px solid ' + (active ? '#002FA7' : '#E2E2E6'),
      background: active ? '#EEF2FF' : 'white', cursor: 'pointer',
      color: active ? '#002FA7' : '#1E2030',
    }}
  >
    {children}
  </button>
);

const NoteDetailPage = () => {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const editorRef = useRef(null);
  const [note, setNote] = useState(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [folderId, setFolderId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [folders, setFolders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [showFonts, setShowFonts] = useState(false);

  const fetchNote = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/planning/notes/${noteId}`, { withCredentials: true });
      setNote(r.data);
      setTitle(r.data.title || '');
      setDate(r.data.date || '');
      setAssignedUserId(r.data.assigned_user_id || '');
      setFolderId(r.data.folder_id || '');
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Note non accessible' : 'Erreur de chargement');
      navigate('/notes-taches');
    }
  }, [noteId, navigate]);

  useEffect(() => { fetchNote(); }, [fetchNote]);

  // Inject the note content into the contentEditable AFTER React has rendered
  // the editor div (otherwise editorRef.current is null on the first paint and
  // the content silently never appears — that's what broke existing notes).
  useEffect(() => {
    if (note && editorRef.current) {
      editorRef.current.innerHTML = sanitize(note.content || '');
    }
    // We intentionally only depend on note.id so user edits don't get clobbered
    // while typing in the same opened note.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note?.id]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API_URL}/api/users/employees`, { params: { include_admins: true }, withCredentials: true })
      .then(r => setEmployees(r.data || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/planning/folders?folder_type=note`, { withCredentials: true })
      .then(r => setFolders(r.data || []))
      .catch(() => {});
  }, [isAdmin]);

  // ---- Persist (manual + on blur) ----
  const persist = useCallback(async () => {
    if (!note) return;
    setSaving(true);
    try {
      const html = sanitize(editorRef.current?.innerHTML || '');
      const body = { title, date, content: html, assigned_user_id: assignedUserId, folder_id: folderId };
      await axios.put(`${API_URL}/api/planning/notes/${noteId}`, body, { withCredentials: true });
    } catch {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  }, [noteId, note, title, date, assignedUserId, folderId]);

  // ---- Toolbar actions (execCommand is deprecated but ubiquitous) ----
  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    try { document.execCommand(cmd, false, value); } catch { /* noop */ }
  };
  const insertCheckbox = () => {
    editorRef.current?.focus();
    const html = '<label class="note-todo" contenteditable="false" style="display:inline-flex;align-items:center;gap:6px;margin-right:8px;cursor:pointer;"><input type="checkbox" /> </label>&nbsp;';
    try { document.execCommand('insertHTML', false, html); } catch { /* noop */ }
  };

  // Interactive checkbox toggle persists in the underlying HTML through the 'checked' attribute
  const handleEditorClick = (e) => {
    if (e.target?.tagName === 'INPUT' && e.target.type === 'checkbox') {
      if (e.target.checked) e.target.setAttribute('checked', 'checked');
      else e.target.removeAttribute('checked');
      // Trigger a save shortly after the user toggles
      setTimeout(persist, 400);
    }
  };

  // ---- Attachments ----
  const onUploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 25 Mo)');
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API_URL}/api/planning/notes/${noteId}/attachment`, fd, { withCredentials: true });
      setNote((n) => ({ ...n, attachments: [...(n?.attachments || []), r.data] }));
      toast.success('Pièce jointe ajoutée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      e.target.value = '';
    }
  };
  const removeAttachment = async (filename) => {
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    try {
      await axios.delete(`${API_URL}/api/planning/notes/${noteId}/attachment/${filename}`, { withCredentials: true });
      setNote((n) => ({ ...n, attachments: (n?.attachments || []).filter(a => a.filename !== filename) }));
    } catch {
      toast.error('Erreur de suppression');
    }
  };

  const handleDeleteNote = async () => {
    if (!window.confirm('Supprimer cette note ? (déplacée dans la corbeille)')) return;
    try {
      await axios.delete(`${API_URL}/api/planning/notes/${noteId}`, { withCredentials: true });
      toast.success('Note supprimée');
      navigate('/notes-taches');
    } catch {
      toast.error('Suppression impossible');
    }
  };

  if (!note) return <div style={{ padding: 24 }}>Chargement…</div>;

  return (
    <div data-testid="note-detail-page" style={{ maxWidth: 920, margin: '0 auto', padding: '16px' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate('/notes-taches')}
          data-testid="note-back-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <ArrowLeft size={16} /> Retour
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-danger" onClick={handleDeleteNote} data-testid="note-delete-btn">
            <Trash size={16} />
          </button>
          <button type="button" className="btn btn-primary" onClick={persist} data-testid="note-save-btn" disabled={saving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FloppyDisk size={16} /> {saving ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Title */}
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={persist}
        placeholder="Titre de la note"
        data-testid="note-title-input"
        style={{
          width: '100%', border: 'none', outline: 'none', fontSize: '1.6rem', fontWeight: 700,
          marginBottom: 6, padding: '6px 2px', background: 'transparent', color: '#0F172A',
        }}
      />

      {/* Meta row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={persist}
          data-testid="note-date-input"
          className="form-input" style={{ width: 160, height: 34 }}
        />
        {isAdmin && (
          <div style={{ minWidth: 220 }}>
            <Select value={assignedUserId || 'none'} onValueChange={(v) => { const nv = v === 'none' ? '' : v; setAssignedUserId(nv); setTimeout(persist, 80); }}>
              <SelectTrigger data-testid="note-assign-select" style={{ height: 34 }}>
                <SelectValue placeholder="Assigner à…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non assignée</SelectItem>
                {employees.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} {u.role === 'admin' ? '(admin)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {isAdmin && (
          <div style={{ minWidth: 200 }}>
            <Select value={folderId || 'none'} onValueChange={(v) => { const nv = v === 'none' ? '' : v; setFolderId(nv); setTimeout(persist, 80); }}>
              <SelectTrigger data-testid="note-folder-select" style={{ height: 34 }}>
                <SelectValue placeholder="Dossier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sans dossier</SelectItem>
                {folders.filter(f => !f.is_trash && f.type === 'note').map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#5E5F66' }}>
          Auteur: {note.author_name || '—'}
        </span>
      </div>

      {/* Toolbar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6,
        padding: '8px 10px', border: '1px solid #E2E2E6', borderRadius: 8,
        background: '#FAFAFA', marginBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
      }}>
        <ToolbarBtn onPress={() => exec('bold')} title="Gras (Ctrl+B)" testId="tb-bold"><TextB size={16} /></ToolbarBtn>
        <ToolbarBtn onPress={() => exec('italic')} title="Italique (Ctrl+I)" testId="tb-italic"><TextItalic size={16} /></ToolbarBtn>
        <ToolbarBtn onPress={() => exec('underline')} title="Souligné (Ctrl+U)" testId="tb-underline"><TextUnderline size={16} /></ToolbarBtn>
        <ToolbarBtn onPress={() => exec('strikeThrough')} title="Barré" testId="tb-strike"><TextStrikethrough size={16} /></ToolbarBtn>
        <span style={{ width: 1, height: 22, background: '#E2E2E6', margin: '0 2px' }} />
        <ToolbarBtn onPress={() => exec('insertUnorderedList')} title="Liste à puces" testId="tb-ul"><ListBullets size={16} /></ToolbarBtn>
        <ToolbarBtn onPress={() => exec('insertOrderedList')} title="Liste numérotée" testId="tb-ol"><ListNumbers size={16} /></ToolbarBtn>
        <ToolbarBtn onPress={insertCheckbox} title="Case à cocher" testId="tb-checkbox"><CheckSquare size={16} /></ToolbarBtn>
        <span style={{ width: 1, height: 22, background: '#E2E2E6', margin: '0 2px' }} />
        <div style={{ position: 'relative' }}>
          <ToolbarBtn onPress={() => { setShowColors(s => !s); setShowFonts(false); }} title="Couleur du texte" testId="tb-color"><PaintBucket size={16} /></ToolbarBtn>
          {showColors && (
            <div style={{ position: 'absolute', top: 38, left: 0, background: 'white', border: '1px solid #E2E2E6', borderRadius: 8, padding: 8, display: 'grid', gridTemplateColumns: 'repeat(4, 22px)', gap: 6, zIndex: 10 }}>
              {COLORS.map(c => (
                <button key={c} type="button"
                  onMouseDown={(e) => { e.preventDefault(); exec('foreColor', c); setShowColors(false); }}
                  style={{ width: 22, height: 22, borderRadius: '50%', background: c, border: '1px solid #E2E2E6', cursor: 'pointer' }}
                  data-testid={`tb-color-${c}`}
                />
              ))}
            </div>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setShowFonts(s => !s); setShowColors(false); }}
            data-testid="tb-font"
            style={{ height: 32, padding: '0 8px', borderRadius: 6, border: '1px solid #E2E2E6', background: 'white', cursor: 'pointer', fontSize: '0.78rem' }}
          >
            Police ▾
          </button>
          {showFonts && (
            <div style={{ position: 'absolute', top: 38, left: 0, background: 'white', border: '1px solid #E2E2E6', borderRadius: 8, padding: 6, zIndex: 10, minWidth: 140 }}>
              {FONTS.map(f => (
                <div key={f.value}
                  onMouseDown={(e) => { e.preventDefault(); exec('fontName', f.value); setShowFonts(false); }}
                  style={{ padding: '6px 8px', cursor: 'pointer', fontFamily: f.value, borderRadius: 4 }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#F4F5F7'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  data-testid={`tb-font-${f.label.toLowerCase()}`}
                >
                  {f.label}
                </div>
              ))}
            </div>
          )}
        </div>
        <span style={{ width: 1, height: 22, background: '#E2E2E6', margin: '0 2px' }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', padding: '4px 8px', borderRadius: 6, border: '1px solid #E2E2E6', background: 'white', fontSize: '0.78rem' }} title="Joindre un fichier">
          <Paperclip size={14} /> Joindre
          <input type="file" onChange={onUploadAttachment} style={{ display: 'none' }} data-testid="note-attach-input" />
        </label>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onBlur={persist}
        onClick={handleEditorClick}
        data-testid="note-editor"
        className="note-editor"
        style={{
          minHeight: 360, padding: '16px',
          border: '1px solid #E2E2E6', borderTop: 'none',
          borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
          background: 'white', outline: 'none', lineHeight: 1.6, fontSize: '0.95rem',
        }}
      />

      {/* Attachments list */}
      {(note.attachments?.length || 0) > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: '0.9rem', color: '#5E5F66', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
            Pièces jointes ({note.attachments.length})
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {note.attachments.map((att) => (
              <div key={att.filename} data-testid={`note-attachment-${att.filename}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid #E2E2E6', borderRadius: 8, background: 'white' }}>
                <Paperclip size={16} color="#5E5F66" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {att.original_name}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
                    {(att.size / 1024).toFixed(1)} Ko · {att.mime}
                  </div>
                </div>
                <a href={`${API_URL}${att.url}`} target="_blank" rel="noopener noreferrer"
                  className="btn btn-secondary btn-sm" data-testid={`note-attach-dl-${att.filename}`}>
                  <Download size={14} />
                </a>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => removeAttachment(att.filename)} data-testid={`note-attach-rm-${att.filename}`}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .note-editor [contenteditable="false"] input[type="checkbox"] { cursor: pointer; transform: scale(1.1); }
        .note-editor ul, .note-editor ol { padding-left: 24px; }
        .note-editor:empty:before {
          content: "Commencez à écrire votre note…";
          color: #9CA3AF;
        }
      `}</style>
    </div>
  );
};

export default NoteDetailPage;
